import React, { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "../lib/api";
import { PageHeader, PageBody, EmptyState } from "../components/Shared";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Trash2, Edit2, KeyRound, UserPlus } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../lib/auth";

const ROLES = ["admin", "user"];

export default function Users() {
  const { user: me } = useAuth();
  const isAdmin = me?.role === "admin";
  const [users, setUsers] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: "", role: "user", password: "" });
  const [creating, setCreating] = useState(false);
  const [createForm, setCreateForm] = useState({ email: "", name: "", role: "user", password: "" });

  const load = () => api.get("/users").then((r) => setUsers(r.data));
  useEffect(() => { load(); }, []);

  const openCreate = () => {
    setCreateForm({ email: "", name: "", role: "user", password: "" });
    setCreating(true);
  };

  const createUser = async (e) => {
    e.preventDefault();
    try {
      await api.post("/users", {
        email: createForm.email.trim().toLowerCase(),
        name: createForm.name.trim(),
        role: createForm.role,
        password: createForm.password,
      });
      toast.success(`User ${createForm.email} created`);
      setCreating(false);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed to create user");
    }
  };

  const openEdit = (u) => {
    setEditing(u);
    setForm({ name: u.name || "", role: u.role || "user", password: "" });
  };

  const save = async (e) => {
    e.preventDefault();
    const patch = {};
    if (form.name !== editing.name) patch.name = form.name;
    if (form.role !== editing.role) patch.role = form.role;
    if (form.password) patch.password = form.password;
    try {
      await api.put(`/users/${editing.id}`, patch);
      toast.success("User updated");
      setEditing(null);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed");
    }
  };

  const del = async (u) => {
    if (u.id === me?.id) return toast.error("You cannot delete your own account");
    if (!window.confirm(`Delete user ${u.email}?`)) return;
    try {
      await api.delete(`/users/${u.id}`);
      toast.success("User deleted");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed");
    }
  };

  return (
    <>
      <PageHeader
        title="Users"
        subtitle="Team access"
        testid="users-title"
        actions={
          isAdmin ? (
            <Button
              onClick={openCreate}
              data-testid="new-user-btn"
              className="rounded-none bg-blue-600 hover:bg-blue-700"
            >
              <UserPlus className="w-4 h-4 mr-2" /> New user
            </Button>
          ) : null
        }
      />
      <PageBody>
        {!isAdmin && (
          <div className="mb-4 text-xs text-slate-500 bg-slate-50 border border-slate-200 px-3 py-2">
            Only administrators can create, edit, or remove users.
          </div>
        )}

        {users.length === 0 ? <EmptyState title="No users" /> :
          <div className="bg-white border border-slate-300">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 border-b border-slate-300">
                <tr className="text-left">
                  <th className="py-2 px-3 label-caps">Name</th>
                  <th className="py-2 px-3 label-caps">Email</th>
                  <th className="py-2 px-3 label-caps">Role</th>
                  <th className="py-2 px-3 label-caps">Joined</th>
                  <th className="py-2 px-3 w-24 text-right label-caps">Actions</th>
                </tr>
              </thead>
              <tbody>
                {users.map((u) => (
                  <tr key={u.id} className="border-b border-slate-200 hover:bg-slate-50" data-testid={`user-row-${u.id}`}>
                    <td className="py-2 px-3">{u.name || "-"} {u.id === me?.id && <span className="label-caps ml-2 text-blue-700">you</span>}</td>
                    <td className="py-2 px-3 font-mono text-slate-600">{u.email}</td>
                    <td className="py-2 px-3">
                      <span className={`text-xs font-mono uppercase tracking-wider px-2 py-0.5 rounded-sm ring-1 ring-inset ${u.role === "admin" ? "bg-blue-50 text-blue-700 ring-blue-700/10" : "bg-slate-100 text-slate-700 ring-slate-700/10"}`}>{u.role}</span>
                    </td>
                    <td className="py-2 px-3 font-mono text-slate-500">{u.created_at?.slice(0, 10)}</td>
                    <td className="py-2 px-3 text-right">
                      <div className="inline-flex gap-2">
                        <button
                          className="text-slate-400 hover:text-blue-700 disabled:opacity-30 disabled:cursor-not-allowed"
                          onClick={() => openEdit(u)}
                          disabled={!isAdmin}
                          data-testid={`edit-user-${u.id}`}
                          title={isAdmin ? "Edit" : "Admin only"}
                        >
                          <Edit2 className="w-4 h-4" />
                        </button>
                        <button
                          className="text-slate-400 hover:text-red-600 disabled:opacity-30 disabled:cursor-not-allowed"
                          onClick={() => del(u)}
                          disabled={!isAdmin || u.id === me?.id}
                          data-testid={`del-user-${u.id}`}
                          title={!isAdmin ? "Admin only" : u.id === me?.id ? "Cannot delete self" : "Delete"}
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        }

        {/* Create user */}
        <Dialog open={creating} onOpenChange={(v) => { if (!v) setCreating(false); }}>
          <DialogContent className="rounded-none max-w-md">
            <DialogHeader><DialogTitle className="font-display tracking-tight">New user</DialogTitle></DialogHeader>
            <form onSubmit={createUser} className="space-y-3">
              <div>
                <Label className="label-caps">Email</Label>
                <Input
                  type="email"
                  required
                  value={createForm.email}
                  onChange={(e) => setCreateForm({ ...createForm, email: e.target.value })}
                  className="rounded-none mt-1"
                  data-testid="new-user-email"
                  autoFocus
                />
              </div>
              <div>
                <Label className="label-caps">Full name</Label>
                <Input
                  required
                  value={createForm.name}
                  onChange={(e) => setCreateForm({ ...createForm, name: e.target.value })}
                  className="rounded-none mt-1"
                  data-testid="new-user-name"
                />
              </div>
              <div>
                <Label className="label-caps">Role</Label>
                <Select value={createForm.role} onValueChange={(v) => setCreateForm({ ...createForm, role: v })}>
                  <SelectTrigger className="rounded-none mt-1" data-testid="new-user-role"><SelectValue /></SelectTrigger>
                  <SelectContent className="rounded-none">{ROLES.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label className="label-caps flex items-center gap-1"><KeyRound className="w-3 h-3" /> Initial password (min 6 chars)</Label>
                <Input
                  type="password"
                  required
                  minLength={6}
                  value={createForm.password}
                  onChange={(e) => setCreateForm({ ...createForm, password: e.target.value })}
                  className="rounded-none mt-1"
                  placeholder="••••••••"
                  data-testid="new-user-password"
                />
                <div className="text-xs text-slate-500 mt-1">Share this with the user — they can change it after signing in.</div>
              </div>
              <DialogFooter>
                <Button
                  type="submit"
                  className="rounded-none bg-blue-600 hover:bg-blue-700"
                  data-testid="new-user-save"
                >
                  Create user
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        {/* Edit user */}
        <Dialog open={!!editing} onOpenChange={(v) => { if (!v) setEditing(null); }}>
          <DialogContent className="rounded-none max-w-md">
            <DialogHeader><DialogTitle className="font-display tracking-tight">Edit user</DialogTitle></DialogHeader>
            {editing && (
              <form onSubmit={save} className="space-y-3">
                <div><Label className="label-caps">Email</Label>
                  <Input value={editing.email} disabled className="rounded-none mt-1 bg-slate-50" /></div>
                <div><Label className="label-caps">Name</Label>
                  <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="rounded-none mt-1" data-testid="edit-user-name" /></div>
                <div><Label className="label-caps">Role</Label>
                  <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                    <SelectTrigger className="rounded-none mt-1" data-testid="edit-user-role"><SelectValue /></SelectTrigger>
                    <SelectContent className="rounded-none">{ROLES.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="label-caps flex items-center gap-1"><KeyRound className="w-3 h-3" /> Reset password (leave blank to keep)</Label>
                  <Input type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="rounded-none mt-1" placeholder="••••••••" data-testid="edit-user-password" />
                </div>
                <DialogFooter><Button type="submit" className="rounded-none bg-blue-600 hover:bg-blue-700" data-testid="edit-user-save">Save</Button></DialogFooter>
              </form>
            )}
          </DialogContent>
        </Dialog>
      </PageBody>
    </>
  );
}
